/**
 * PowerSync Database Singleton — Renderer Process
 *
 * Electron build: PowerSync now runs in the main process (native
 * @powersync/node + better-sqlite3, significantly faster than WASM SQLite).
 * `db` here is `ElectronPowerSyncFacade`, a thin proxy over the
 * `window.powersync` IPC bridge — see electronDb.ts.
 *
 * Web (Vercel) build: unchanged — @powersync/web still runs directly in the
 * browser (OPFS + WASM SQLite), since there is no main process to host it.
 * All of its construction and auto-connect/reconnect wiring is guarded so it
 * only runs when `!isElectron()` — the constructor and its side effects
 * (network calls, event listeners, intervals) must never execute in the
 * Electron build.
 *
 * Both branches implement the shared `PowerSyncFacade` interface
 * (facadeTypes.ts) so every other renderer module that touches `db` works
 * unchanged regardless of which platform is active.
 */

import { PowerSyncDatabase } from '@powersync/web';
import { APP_VERSION } from 'utils/constants';

import { supabase } from '@/renderer/services/supabase';
import { isElectron } from '@/renderer/util';

import { AppSchema } from './AppSchema';
import { ElectronPowerSyncFacade } from './electronDb';
import {
  isEmptyResultError,
  PowerSyncFacade,
  PowerSyncTransactionFacade,
} from './facadeTypes';
import { SupabaseConnector, syncEvents } from './supabaseConnector';
import { SwappablePowerSyncFacade } from './swappableDb';

// Thin pass-through wrapper around the real @powersync/web instance so it
// satisfies the exact same `PowerSyncFacade` interface as the Electron IPC
// proxy — the connector is bound internally so callers never need to pass
// one (mirrors ElectronPowerSyncFacade.connect(), which also takes none).
class WebPowerSyncFacade implements PowerSyncFacade {
  private readonly inner: PowerSyncDatabase;

  private readonly boundConnector: SupabaseConnector;

  constructor(inner: PowerSyncDatabase, boundConnector: SupabaseConnector) {
    this.inner = inner;
    this.boundConnector = boundConnector;
  }

  get currentStatus() {
    return this.inner.currentStatus;
  }

  execute(sql: string, params?: unknown[]) {
    return this.inner.execute(sql, params);
  }

  get<T = Record<string, unknown>>(sql: string, params?: unknown[]) {
    return this.inner.get<T>(sql, params);
  }

  getAll<T = Record<string, unknown>>(sql: string, params?: unknown[]) {
    return this.inner.getAll<T>(sql, params);
  }

  getUploadQueueStats() {
    return this.inner.getUploadQueueStats();
  }

  writeTransaction<T>(
    callback: (tx: PowerSyncTransactionFacade) => Promise<T>,
  ) {
    return this.inner.writeTransaction(async (tx) => {
      const normalized: PowerSyncTransactionFacade = {
        execute: (sql, params) => tx.execute(sql, params),
        get: async <R>(sql: string, params?: unknown[]) => {
          try {
            return await tx.get<R>(sql, params);
          } catch (err) {
            if (isEmptyResultError(err)) return undefined as R;
            throw err;
          }
        },
      };
      return callback(normalized);
    });
  }

  registerListener(listener: {
    statusChanged?: (s: PowerSyncFacade['currentStatus']) => void;
  }) {
    return this.inner.registerListener(listener);
  }

  onChange(
    listener: { onChange: (event: { changedTables: string[] }) => void },
    options?: unknown,
  ) {
    return this.inner.onChange(listener, options as { tables: string[] });
  }

  connect(): Promise<void> {
    return this.inner.connect(this.boundConnector);
  }
}

// Best-effort per-outlet scoping for the web/fallback engine's local file,
// mirroring the main process's per-outlet native db files
// (dbFilenameForOutlet in ../../main/lib/powersync/database.ts). Unlike the
// main process, this engine is constructed once at module load — before any
// outlet is necessarily known via Redux — so the outlet id is read directly
// from the same localStorage key outletSlice.ts's setSelectedOutletId writes
// (persisted from whatever the last selection was, including a prior
// session). Without this, every fallback activation used a single flat
// filename shared across every outlet ever signed into on this machine,
// breaking the per-outlet data isolation the native engine provides and
// starting from an empty cache on every fallback even for an outlet that
// had previously built up its own web-fallback cache.
function webDbFilenameForCurrentOutlet(): string {
  try {
    const raw = localStorage.getItem('outlet');
    if (!raw) return 'smartpos-sales.db';
    const outletId = JSON.parse(raw) as string | null;
    if (!outletId) return 'smartpos-sales.db';
    const sanitized = outletId.replace(/[^a-zA-Z0-9_-]/g, '_');
    return `smartpos-sales-web-${sanitized}.db`;
  } catch {
    return 'smartpos-sales.db';
  }
}

// Tears down the previous web-fallback instance's interval/listener/
// subscription before a new one is created — see createWebPowerSync()'s
// call to this below. Without it, every native→fallback→recover→fallback
// cycle (driven by ensureOutletSync()'s automatic offline retry calling
// tryRecoverNativeEngine() on every attempt) permanently leaked another
// 15-minute setInterval, visibilitychange listener, and Supabase auth
// subscription — each holding a closure over the abandoned
// PowerSyncDatabase/WASM-SQLite instance it was created for, so none of
// them could ever be garbage collected.
let disposeCurrentWebPowerSync: (() => void) | null = null;

function createWebPowerSync(): PowerSyncFacade {
  disposeCurrentWebPowerSync?.();

  const webConnector = new SupabaseConnector();

  const webDb = new PowerSyncDatabase({
    schema: AppSchema,
    database: { dbFilename: webDbFilenameForCurrentOutlet() },
    // This webpack build has no experiments.outputModule, so @powersync/web's
    // default `new SharedWorker(new URL('./WASQLiteDB.worker.js', import.meta.url), { type: 'module' })`
    // worker chunk isn't emitted as a loadable ES module — the worker never
    // initializes wa-sqlite, and every query fails with sqlite3_open_v2.
    // Running wa-sqlite on the main thread instead sidesteps worker bundling
    // entirely; safe here since the default VFS (IDBBatchAtomicVFS) doesn't
    // require OPFS/dedicated workers. Trade-off: no multi-tab coordination,
    // acceptable for a POS station that's normally one tab per device.
    flags: { useWebWorker: false },
  });

  // Connect when there is already an active session (e.g. after app restart
  // with persisted Supabase session in localStorage).
  supabase.auth
    .getSession()
    .then(({ data: { session } }) => {
      if (!session) return null;
      return webDb.connect(webConnector);
    })
    .catch((err) => console.error('[PowerSync] Initial connect failed:', err));

  // Auto-connect / disconnect based on Supabase auth state changes. Local
  // data is scoped by outlet_id, not by user, so signing out only stops the
  // sync stream — it must never clear local data, since a different staff
  // member signing in next at the same outlet should reuse the warmed cache
  // instead of forcing a full resync.
  const {
    data: { subscription: authSubscription },
  } = supabase.auth.onAuthStateChange((event, session) => {
    if (session && (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED')) {
      webDb
        .connect(webConnector)
        .catch((err) => console.error('[PowerSync] connect failed:', err));
    } else if (event === 'SIGNED_OUT') {
      webDb
        .disconnect()
        .catch((err) => console.error('[PowerSync] disconnect failed:', err));
    }
  });

  // Force a reconnect for an already-connected session, regardless of
  // connection health. `db.connect()` always tears down and renegotiates a
  // fresh sync stream, which is the only way an already-connected client picks
  // up a newly redeployed powersync.yaml (row-filter/stream changes) — the
  // existing SIGNED_IN/TOKEN_REFRESHED reconnects are incidental (hourly at
  // best) and SyncStatusChip's auto-reconnect loop only runs while offline.
  const forceReconnectIfSignedIn = async () => {
    const {
      data: { session },
    } = await supabase.auth.getSession();
    if (!session) return;
    try {
      await webDb.connect(webConnector);
    } catch (err) {
      console.error('[PowerSync] periodic reconnect failed:', err);
    }
  };

  const RECONNECT_INTERVAL_MS = 15 * 60 * 1000; // 15 minutes
  const reconnectIntervalId = setInterval(
    forceReconnectIfSignedIn,
    RECONNECT_INTERVAL_MS,
  );

  // Also reconnect whenever the app window regains focus/visibility (e.g. an
  // Electron window left idle in the background for a while), so a config
  // change deployed while the app was backgrounded is picked up right away.
  const handleVisibilityChange = () => {
    if (document.visibilityState === 'visible') {
      forceReconnectIfSignedIn();
    }
  };
  document.addEventListener('visibilitychange', handleVisibilityChange);

  disposeCurrentWebPowerSync = () => {
    clearInterval(reconnectIntervalId);
    document.removeEventListener('visibilitychange', handleVisibilityChange);
    authSubscription.unsubscribe();
    webDb.disconnect().catch(() => {});
  };

  return new WebPowerSyncFacade(webDb, webConnector);
}

// ── Swappable facade + web fallback ─────────────────────────────────────────
//
// On Electron the fast path is the native main-process engine
// (ElectronPowerSyncFacade). But if that engine can't run in a packaged build
// (native addon load failure, corrupt local DB that won't reopen, connect
// failure), we fall back to running @powersync/web *inside the renderer* — the
// same client the browser build uses. It's slower (WASM SQLite) but self-
// contained, so the Sales page keeps working instead of showing empty screens.
//
// `db` is a stable SwappablePowerSyncFacade (see swappableDb.ts) whose inner
// implementation can be swapped at runtime without any caller (`import { db }`)
// noticing — it re-attaches live listeners to the new engine on swap.

// Remembers, per app version, that the native engine was found unusable on this
// machine — so subsequent launches skip the native attempt and go straight to
// the web client instead of repeating the failure dance every boot. Keyed by
// version so a later build that fixes the native engine automatically reclaims
// the fast path.
const FALLBACK_FLAG_KEY = 'smartpos_powersync_fallback';

function nativeKnownBroken(): boolean {
  try {
    return localStorage.getItem(FALLBACK_FLAG_KEY) === APP_VERSION;
  } catch {
    return false;
  }
}

function rememberNativeBroken(): void {
  try {
    localStorage.setItem(FALLBACK_FLAG_KEY, APP_VERSION);
  } catch {
    // localStorage unavailable — non-fatal; fallback still works this session.
  }
}

function createInitialFacade(): PowerSyncFacade {
  if (!isElectron()) return createWebPowerSync();
  // Electron: prefer the native engine unless a prior run on this exact build
  // already proved it can't run here.
  if (nativeKnownBroken()) return createWebPowerSync();
  return new ElectronPowerSyncFacade();
}

const swappableDb = new SwappablePowerSyncFacade(createInitialFacade());

export const db: PowerSyncFacade = swappableDb;

/** Whether `db` is currently backed by the in-renderer @powersync/web client. */
let usingWebFallback = !isElectron() || nativeKnownBroken();

export function isUsingWebFallback(): boolean {
  return usingWebFallback;
}

/**
 * Switch `db` to the in-renderer @powersync/web client. Idempotent and safe to
 * call from multiple triggers (native-engine-failed IPC event, the repurposed
 * outlet-unavailable watchdog). No-op on the web build (already web) or once a
 * fallback is active.
 */
export function enableWebFallback(): void {
  if (usingWebFallback) return;
  if (!isElectron()) return;

  // Best-effort check of the about-to-be-abandoned native engine's upload
  // queue, kicked off *before* the swap below while `db` still proxies to it.
  // The native engine has its own separate local SQLite file — once we swap
  // to the web engine's independent OPFS/WASM database, any writes still
  // queued here are orphaned: never uploaded, never visible to the app again
  // until someone manually recovers the abandoned native file. If the native
  // engine already crashed hard, this query may itself fail — treat that as
  // "unknown", not "zero", since we can't prove nothing was queued when we
  // can't even ask.
  const pendingQueueCheck = db
    .getUploadQueueStats()
    .then((stats) => stats.count)
    .catch(() => -1);

  try {
    const webFacade = createWebPowerSync();
    swappableDb.swap(webFacade);
    usingWebFallback = true;
    rememberNativeBroken();
    console.info(
      '[PowerSync] Switched to in-renderer @powersync/web fallback.',
    );
  } catch (err) {
    console.error('[PowerSync] Failed to start web fallback:', err);
    return;
  }

  pendingQueueCheck
    .then((count) => {
      if (count === 0) return undefined;
      const description = count < 0 ? 'an unknown number of' : `${count}`;
      console.warn(
        `[PowerSync] Switched to web fallback with ${description} unsynced local write(s) left behind on the abandoned native database — they will not be uploaded until the native engine is restored.`,
      );
      return syncEvents.dispatchEvent(
        new CustomEvent('sync-engine-fallback-data-at-risk', {
          detail: { pendingCount: count },
        }),
      );
    })
    .catch((err) => {
      console.error('[PowerSync] Fallback data-at-risk check failed:', err);
    });
}

/**
 * Attempts to undo enableWebFallback() — clears the "native known broken" flag
 * and swaps `db` back onto a fresh ElectronPowerSyncFacade. Called by
 * ensureOutletSync() on every manual "Resync Now" (and every other entry
 * point it's used from), so an outlet that got stranded on the fallback
 * engine by a merely recoverable server-side sync-rule error — not a
 * genuinely broken native engine — isn't stuck there until an app version
 * bump. If the native engine is still actually broken, the normal failure
 * path re-trips enableWebFallback() naturally; this just makes manual retry
 * actually re-attempt the fast path instead of only reconnecting a database
 * nobody's reading. No-op on the web build or when not currently on fallback.
 */
export function tryRecoverNativeEngine(): void {
  if (!isElectron()) return;
  if (!usingWebFallback) return;
  try {
    localStorage.removeItem(FALLBACK_FLAG_KEY);
  } catch {
    // localStorage unavailable — non-fatal, we still attempt the swap below.
  }
  swappableDb.swap(new ElectronPowerSyncFacade());
  usingWebFallback = false;
  console.info('[PowerSync] Attempting recovery back to the native engine.');
}

// ── Corrupted local (web-engine) database recovery ──────────────────────────
//
// ipcBridge.ts's circuit breaker dispatches 'local-db-unrecoverable' after a
// burst of local query failures matching a corruption signature (e.g.
// sqlite3_open_v2). That failure mode is specific to whichever engine is
// currently backed by @powersync/web's IndexedDB storage — the plain web
// build always, or the Electron build while running on enableWebFallback().
// The PowerSync local database is only a re-downloadable cache (the sync
// service is the source of truth), so deleting and recreating it is always
// safe — worst case is a fresh full resync, the same as a first-ever login.
// Native-engine failures on Electron are handled separately by
// enableWebFallback()/tryRecoverNativeEngine() and never reach here.
let recoveringLocalDatabase = false;

async function recoverLocalDatabase(): Promise<void> {
  if (!usingWebFallback) return; // corruption signal doesn't apply to the native engine
  if (recoveringLocalDatabase) return;
  recoveringLocalDatabase = true;

  try {
    disposeCurrentWebPowerSync?.();

    const dbFilename = webDbFilenameForCurrentOutlet();
    await new Promise<void>((resolve) => {
      const request = indexedDB.deleteDatabase(dbFilename);
      request.onsuccess = () => resolve();
      request.onerror = () => resolve(); // best-effort — still attempt recreation below
      request.onblocked = () => resolve();
    });

    const freshFacade = createWebPowerSync();
    swappableDb.swap(freshFacade);
    console.info(
      '[PowerSync] Recovered from a corrupted local database — recreated fresh.',
    );
    syncEvents.dispatchEvent(new CustomEvent('local-db-recovered'));
  } catch (err) {
    console.error('[PowerSync] Local database recovery failed:', err);
  } finally {
    recoveringLocalDatabase = false;
  }
}

syncEvents.addEventListener('local-db-unrecoverable', () => {
  recoverLocalDatabase().catch((err) => {
    console.error('[PowerSync] Local database recovery handler failed:', err);
  });
});
